import React, { useState } from 'react';

import * as Sentry from '@sentry/browser';
import { loadStripe } from '@stripe/stripe-js';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';

import { LegacyBasePlanSelectorFormBody } from '../../../../account/components/legacy-subscription/legacy-base-plan-selector-form-body';
import { updateBasePlan } from '../../../actions/update-base-plan';

import * as Api from '../../../../api/api';
import { UiButton } from '../../../../ui/ui-button';
import { UiDialog } from '../../../../ui/ui-dialog';

import { PRICING_VERSION } from '../../../../account/utils/pricing-version';
import { STRIPE_PUBLIC_KEY } from '../../../../config/config';
import { ACCOUNT_STATUS } from '../../../../util/account-status';
import { SubscriptionCadence } from '../../../../util/subscription-cadence';

type InputProps = AccountStatusTransitionDialogProps & {
  isEdit?: boolean;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { account }: InputProps,
) => ({
  actions: {
    updateBasePlan: (
      subscriptionCadence: SubscriptionCadence,
      baseMonthlyRateStripePlanId: string,
      isPartnerReferral?: boolean,
    ) =>
      dispatch(
        updateBasePlan(account.accountId, {
          subscriptionCadence,
          baseMonthlyRateStripePlanId,
          isPartnerReferral,
        }),
      ),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const PlanConfirmationDialogBody = connect(
  undefined,
  mapDispatchToProps,
)((props: Props) => {
  const { cancelTransition, account, org, isEdit, handleTransitionConfirmed, actions } = props;

  const [selectedSubscriptionCadence, setSelectedSubscriptionCadence] =
    useState<SubscriptionCadence | null>(account?.subscriptionCadence);
  const [selectedBaseMonthlyRateStripePlanId, setSelectedBaseMonthlyRateStripePlanId] = useState<
    string | null
  >(account?.baseMonthlyRateStripePlanId);
  const [isPartnerReferral, setIsPartnerReferral] = useState<boolean>(
    Boolean(account?.isPartnerReferral),
  );
  const [submitting, setIsSubmitting] = useState<boolean>(false);

  if (!account) {
    return null;
  }

  const changePlan = (
    subscriptionCadence: SubscriptionCadence | null,
    baseMonthlyRateStripePlanId: string | null,
  ) => {
    setSelectedSubscriptionCadence(subscriptionCadence);
    setSelectedBaseMonthlyRateStripePlanId(baseMonthlyRateStripePlanId);
  };

  const handleIsPartnerReferralChange = () => {
    setIsPartnerReferral(!isPartnerReferral);
  };

  const handleSubmit = async () => {
    if (!selectedSubscriptionCadence || !selectedBaseMonthlyRateStripePlanId) {
      return;
    }
    setIsSubmitting(true);
    if (isEdit) {
      await actions.updateBasePlan(
        selectedSubscriptionCadence,
        selectedBaseMonthlyRateStripePlanId,
        isPartnerReferral,
      );
      cancelTransition();
    } else if (
      !account.hasPaymentInfo &&
      org &&
      selectedSubscriptionCadence === SubscriptionCadence.MONTHLY
    ) {
      // Update the plan and cadence to what was selected
      await actions.updateBasePlan(
        selectedSubscriptionCadence,
        selectedBaseMonthlyRateStripePlanId,
        isPartnerReferral,
      );

      // Initiate a checkout (for the now updated plan) where credit card information will be collected
      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) {
        Sentry.captureMessage('Stripe checkout redirection failed');
        return;
      }
      const response = await Api.post(
        `/account/${account.accountId}/create-subscription-checkout-session`,
        {
          body: JSON.stringify({ orgId: org.org_id }),
        },
      );
      const session = await response.json();

      await stripe.redirectToCheckout({
        sessionId: session.id,
      });
    } else if (handleTransitionConfirmed) {
      // No payment info needed, so just execute the status and plan change
      await handleTransitionConfirmed({
        pricingVersionId:
          account.pricingVersionId === PRICING_VERSION.V1 ? PRICING_VERSION.V1 : PRICING_VERSION.V2,
        isPartnerReferral,
        subscriptionCadence: selectedSubscriptionCadence,
        baseMonthlyRateStripePlanId: selectedBaseMonthlyRateStripePlanId,
      });
      cancelTransition();
    }
  };

  return (
    <>
      <UiDialog.Content>
        <FormGroup>
          <LegacyBasePlanSelectorFormBody
            subscriptionCadence={selectedSubscriptionCadence}
            baseMonthlyRateStripePlanId={selectedBaseMonthlyRateStripePlanId}
            accountMissingPaymentInfo={!account.hasPaymentInfo}
            onChange={changePlan}
          />
        </FormGroup>
        {(account.status === ACCOUNT_STATUS.PRE_TRIAL ||
          account.status === ACCOUNT_STATUS.TRIAL ||
          account.status === ACCOUNT_STATUS.TRIAL_LAPSED) && (
          <div className="u-margin-top-sm u-flex-center">
            <Checkbox
              className="u-no-padding"
              checked={isPartnerReferral}
              onChange={handleIsPartnerReferralChange}
            />
            <div className="typo-title u-margin-left-md">
              This is a partner referral (33% off for 3 months)
            </div>
          </div>
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        <div className="u-spread">
          <UiButton variant="secondary" onClick={cancelTransition} className="u-margin-sm">
            Cancel
          </UiButton>
          <UiButton
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            className="u-margin-sm"
          >
            {isEdit ? 'Save' : 'Subscribe'}
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
});
